import { Button } from "@secure-vault/ui";
import { useAuth } from "@/lib/auth-context";
import {
  createUnlockPetition,
  getVaultPetitionDocKey,
  getUnlockStatus,
  listTrusteeUnlockPetitions,
  listTrusteeShares,
  submitUnlockPartial,
  UnlockPetitionItem,
  ShareItem,
} from "@secure-vault/api";
import { base64ToBytes, decryptWithKey } from "@/lib/crypto/masterKey";
import { encryptECIES } from "@/lib/crypto/escrow";
import { partialDecrypt } from "@/lib/crypto/threshold";
import { parseHashParams, scrubHash } from "@/lib/url";
import { ExecutorAggregateContainer } from "./executor-aggregate-container";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  Loader2,
  Lock,
  ShieldAlert,
  ShieldCheck,
  Upload,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useIdentityVerification } from "../hooks/use-identity-verification";
import { IdentityVerificationBadge } from "../components/identity-verification-badge";

/**
 * Trustee unlock portal (Phase 3 of NEW_VAULT.md).
 *
 * A trustee arrives via a secure URL carrying their `Trustee_Secret` and the active
 * unlock `sessionId`. They decrypt their shard locally, compute a partial decryption of
 * the master-key ciphertext, and submit only the opaque partial point. If the trustee is
 * the designated Executor, the aggregation panel is shown so they can reconstruct the MK
 * and download files. The unlock-petition path remains as the contingency route.
 */
export function TrusteeUnlockContainer() {
  const { user } = useAuth();
  const trusteeEmail = user?.email || "";
  const navigate = useNavigate();

  // Secret material arrives in the URL *fragment* (#secret=…&session=…&clientId=…),
  // never the query string — fragments are not sent to the server. Captured once on
  // mount, then the fragment is scrubbed from the address bar / history.
  const [urlClientId] = useState<string>(
    () => parseHashParams().clientId || ""
  );
  const [urlSessionId] = useState<string>(
    () => parseHashParams().session || ""
  );
  const [urlTrusteeSecret] = useState<string>(
    () => parseHashParams().secret || ""
  );

  useEffect(() => {
    scrubHash();
  }, []);

  const [shares, setShares] = useState<ShareItem[]>([]);
  const [petitions, setPetitions] = useState<UnlockPetitionItem[]>([]);
  const [, setLoading] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState<string>(urlClientId);
  const [deathCertificate, setDeathCertificate] = useState<File | null>(null);
  const [submittingPetition, setSubmittingPetition] = useState(false);

  // Unlock-session state (the session id arrives via the secure URL fragment)
  const sessionId = urlSessionId;
  const [trusteeSecret] = useState<string>(urlTrusteeSecret);
  const [submittingPartial, setSubmittingPartial] = useState(false);
  const [partialSubmitted, setPartialSubmitted] = useState(false);
  const [isExecutor, setIsExecutor] = useState(false);

  // Unlock step-up verification (the primary, fail-closed identity gate). A fresh,
  // verified `unlock`-purpose check bound to THIS session is required before a
  // partial is accepted — this is the actual attack window (inbox takeover / stale
  // address). The backend re-checks it in POST /unlock/:session/partial.
  const {
    status: unlockIdStatus,
    running: verifyingIdentity,
    verify: verifyUnlockIdentity,
  } = useIdentityVerification({
    clientId: selectedClientId,
    purpose: "unlock",
    unlockSessionId: sessionId,
    enabled: !!sessionId && !!selectedClientId,
  });
  const identityVerified = unlockIdStatus === "verified";

  const handleVerifyIdentity = async () => {
    const result = await verifyUnlockIdentity();
    if (result === "verified") {
      toast.success("Your identity has been confirmed. You can now continue.");
    } else if (result === "failed" || result === "expired") {
      toast.error("We couldn't confirm your identity. Please try again.");
    }
  };

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [shareList, petitionList] = await Promise.all([
        listTrusteeShares(trusteeEmail),
        listTrusteeUnlockPetitions(),
      ]);
      setShares(shareList);
      setPetitions(petitionList);
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't load this vault. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [trusteeEmail]);

  useEffect(() => {
    if (trusteeEmail) loadInitialData();
  }, [trusteeEmail, loadInitialData]);

  useEffect(() => {
    if (urlClientId) setSelectedClientId(urlClientId);
  }, [urlClientId]);

  // When arriving with a session, resolve whether the caller is the executor.
  useEffect(() => {
    if (!sessionId) return;
    getUnlockStatus(sessionId)
      .then((s) => {
        setIsExecutor(s.is_executor);
        setSelectedClientId((prev) => (prev ? prev : s.client_id));
      })
      .catch((err) => console.warn("Failed to load unlock status:", err));
  }, [sessionId]);

  const currentShare = shares.find((s) => s.client_id === selectedClientId);
  const currentPetition = petitions.find(
    (p) => p.client_id === selectedClientId
  );

  const handleSubmitPetition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId || !deathCertificate || !trusteeEmail) return;

    setSubmittingPetition(true);
    try {
      toast.loading("Preparing your request...", {
        id: "petition-flow",
      });
      // Encrypt the document in-browser to the vault's petition-document public key so the
      // server only ever stores ciphertext. Only the advisory (org members) — and the
      // platform — can later decrypt it.
      const publicKey = await getVaultPetitionDocKey(selectedClientId);
      const documentBytes = new Uint8Array(
        await deathCertificate.arrayBuffer()
      );
      const encryptedBase64 = await encryptECIES(documentBytes, publicKey);
      const encryptedPacket = base64ToBytes(encryptedBase64);
      await createUnlockPetition(
        selectedClientId,
        trusteeEmail,
        encryptedPacket,
        deathCertificate.type || "application/octet-stream"
      );
      toast.success("Your request has been sent for review.", {
        id: "petition-flow",
      });
      setDeathCertificate(null);
      await loadInitialData();
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't send your request. Please try again.", {
        id: "petition-flow",
      });
    } finally {
      setSubmittingPetition(false);
    }
  };

  /**
   * Decrypt the trustee's shard with the URL-borne Trustee_Secret, compute the partial
   * decryption, and submit it. The shard and secret never leave the browser.
   */
  const handleSubmitPartial = async () => {
    if (!currentShare || !sessionId || !trusteeSecret) {
      toast.error(
        "This link is incomplete. Ask the adviser to send you a new one."
      );
      return;
    }
    if (!identityVerified) {
      toast.error("Confirm your identity before continuing.");
      return;
    }
    setSubmittingPartial(true);
    try {
      // 1. Recover the public C1 point from the session.
      const status = await getUnlockStatus(sessionId);

      // 2. Decrypt the shard locally using the Trustee_Secret. The shard is a base64url
      //    scalar string (as produced by scalarSplit), stored AES-GCM-encrypted.
      const secretBytes = base64ToBytes(trusteeSecret);
      const shardBytes = await decryptWithKey(
        currentShare.encrypted_shard,
        secretBytes
      );
      const shardB64Url = new TextDecoder().decode(shardBytes);

      // 3. Compute the partial decryption P_i = x_i · C1.
      const partial = await partialDecrypt(shardB64Url, status.c1);

      // 4. Submit only the opaque partial point.
      await submitUnlockPartial(sessionId, {
        share_index: currentShare.share_index,
        partial,
      });
      setPartialSubmitted(true);
      toast.success("Your approval has been recorded.");
    } catch (err: unknown) {
      console.error(err);
      toast.error("We couldn't record your approval. Please try again.");
    } finally {
      setSubmittingPartial(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/trustee" })}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Help open a vault
          </h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {sessionId
              ? "Confirm your identity, then approve this request."
              : "Request access if you cannot contact the adviser."}
          </p>
        </div>
      </div>

      {selectedClientId && (
        <>
          {/* Partial decryption panel (secure-URL unlock flow) */}
          {sessionId ? (
            <div className="bg-card border-border space-y-6 rounded-xl border p-6 shadow-sm">
              <div className="flex items-center gap-2.5 border-b pb-2">
                <ShieldCheck className="text-primary h-5 w-5" />
                <h2 className="text-base font-semibold">
                  Approve this request
                </h2>
              </div>
              <p className="text-muted-foreground text-xs leading-relaxed">
                You have been asked to help open this vault. Confirm your
                identity, then approve the request below.
              </p>

              {!trusteeSecret && (
                <div className="flex gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-500">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  This link is incomplete. Ask the adviser to send you a new
                  one.
                </div>
              )}

              {/* Step-up identity gate — required before the partial is accepted. */}
              <div className="border-border bg-muted/20 space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="text-primary h-4 w-4" />
                    <span className="text-xs font-semibold">
                      Confirm your identity
                    </span>
                  </div>
                  <IdentityVerificationBadge status={unlockIdStatus} />
                </div>
                <p className="text-muted-foreground text-[11px] leading-relaxed">
                  This protects the vault by making sure the person responding
                  is really you.
                </p>
                {!identityVerified && (
                  <Button
                    onClick={handleVerifyIdentity}
                    disabled={verifyingIdentity}
                    variant="outline"
                    className="w-full text-xs"
                  >
                    {verifyingIdentity ? (
                      <>
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        Checking…
                      </>
                    ) : (
                      <>
                        <BadgeCheck className="mr-1.5 h-4 w-4" />
                        Confirm your identity
                      </>
                    )}
                  </Button>
                )}
              </div>

              {partialSubmitted ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-500">
                  <CheckCircle2 className="h-4 w-4" />
                  Your approval has been recorded. Thank you. The Executor will
                  be notified when the documents are ready.
                </div>
              ) : (
                <Button
                  onClick={handleSubmitPartial}
                  disabled={
                    submittingPartial ||
                    !currentShare ||
                    !trusteeSecret ||
                    !identityVerified
                  }
                  className="w-full text-xs"
                >
                  {submittingPartial ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Recording your approval...
                    </>
                  ) : !identityVerified ? (
                    "Confirm your identity to continue"
                  ) : (
                    "Approve access"
                  )}
                </Button>
              )}
            </div>
          ) : null}

          {/* Executor aggregation panel */}
          {sessionId && isExecutor ? (
            <ExecutorAggregateContainer
              sessionId={sessionId}
              clientId={selectedClientId}
            />
          ) : null}

          {/* Unlock petition (contingency) — only when no adviser-opened
              unlock session is in progress. */}
          {!sessionId &&
            (!currentPetition || currentPetition.status === "rejected") && (
              <form
                onSubmit={handleSubmitPetition}
                className="bg-card border-border space-y-6 rounded-xl border p-6 shadow-sm"
              >
                <div className="flex items-center gap-2.5 border-b pb-2">
                  <ShieldAlert className="text-primary h-5 w-5" />
                  <h2 className="text-base font-semibold">
                    Request access without the adviser
                  </h2>
                </div>

                <div className="flex gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-xs leading-relaxed text-amber-500">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <div>
                    <strong className="mb-0.5 block font-semibold">
                      Important
                    </strong>
                    Use this only if you cannot contact the adviser. You will
                    need evidence that the vault owner has died. False requests
                    may have serious legal consequences.
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-foreground text-xs font-semibold">
                    Death certificate
                  </label>
                  <div className="border-border/80 hover:border-primary/50 bg-muted/10 relative flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center transition-all">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        setDeathCertificate(e.target.files?.[0] || null)
                      }
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      required
                    />
                    <Upload className="text-muted-foreground/60 mb-2 h-8 w-8" />
                    <span className="text-foreground text-xs font-medium">
                      {deathCertificate
                        ? deathCertificate.name
                        : "Choose a PDF or image"}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submittingPetition}
                  className="w-full text-xs"
                >
                  {submittingPetition ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Sending request...
                    </>
                  ) : (
                    "Send request"
                  )}
                </Button>
              </form>
            )}

          {/* The unlock petition is a contingency path. Once the adviser has
              opened an unlock session (secure link carries a session id), that
              session supersedes the petition — don't surface its pending status. */}
          {!sessionId &&
            currentPetition &&
            currentPetition.status === "pending" && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-500">
                <Lock className="h-4 w-4" />
                Your request is being reviewed.
              </div>
            )}
        </>
      )}
    </div>
  );
}
